// src/follower/components/BrokerLinkModal.jsx
// Phase 1 — mock submit. Replace mockLinkBroker with real Lambda POST in Phase 2.

import React, { useState } from "react";
import axios from "axios";
import config from "../../config";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    bg:      "#08101e",
    bg1:     "#0c1525",
    bg2:     "#101b2e",
    border:  "#1e2d42",
    border2: "#243548",
    text:    "#e8edf5",
    body:    "#b8c8dc",
    meta:    "#6a7d95",
    green:   "#00e896",
    cyan:    "#00c8ff",
    amber:   "#ffb340",
    red:     "#ff4466",
};
const FONT = "'DM Mono', monospace";

// ─── API call — Lambda POST /follower/link-broker ────────────────────────────
async function apiLinkBroker(payload) {
    const token = sessionStorage.getItem('token');
    const response = await axios.post(
        config.API_BASE_URL + '/follower/link-broker',
        payload,
        {
            headers: {
                'x-api-key':      config.API_KEY,
                'Authorization':  `Bearer ${token}`,
                'Content-Type':   'application/json',
            },
        }
    );
    return response.data;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const Label = ({ children }) => (
    <label style={{
    display:       "block",
    fontFamily:    FONT,
    fontSize:      9,
    fontWeight:    500,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color:         C.meta,
    marginBottom:  6,
  }}>
        {children}
    </label>
);

const Input = ({ value, onChange, placeholder, type = "text", disabled }) => (
    <input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        style={{
      width:         "100%",
      background:    C.bg,
      border:        `1px solid ${C.border}`,
      borderRadius:  4,
      color:         C.text,
      fontFamily:    FONT,
      fontSize:      11,
      padding:       "9px 12px",
      outline:       "none",
      letterSpacing: ".3px",
      boxSizing:     "border-box",
      transition:    "border-color .15s",
      opacity:       disabled ? 0.45 : 1,
    }}
        onFocus={e  => { if (!disabled) e.target.style.borderColor = C.cyan; }}
        onBlur={e   => { e.target.style.borderColor = C.border; }}
    />
);

const FieldGroup = ({ label, required, children }) => (
    <div style={{ marginBottom: 16 }}>
        <Label>{label}{required && <span style={{ color: C.cyan, marginLeft: 3 }}>*</span>}</Label>
        {children}
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export default function BrokerLinkModal({ onSuccess, onCancel }) {
    const [paperKey,    setPaperKey]    = useState("");
    const [paperSecret, setPaperSecret] = useState("");
    const [liveKey,     setLiveKey]     = useState("");
    const [liveSecret,  setLiveSecret]  = useState("");
    const [mode,        setMode]        = useState("paper");   // "paper" | "live"
    const [consented,   setConsented]   = useState(false);
    const [loading,     setLoading]     = useState(false);
    const [error,       setError]       = useState(null);

    const canSubmit =
        paperKey.trim() &&
        paperSecret.trim() &&
        consented &&
        !loading;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setError(null);
        setLoading(true);

        try {
            await apiLinkBroker({
                broker:            "alpaca",
                trading_mode:      mode,
                paper_api_key:     paperKey.trim(),
                paper_secret_key:  paperSecret.trim(),
                live_api_key:      liveKey.trim()    || null,
                live_secret_key:   liveSecret.trim() || null,
            });
            onSuccess();
        } catch (err) {
            setLoading(false);
            setError("Failed to link broker. Please check your keys and try again.");
        }
    };

    return (
        <>
        {/* ── CSS ── */}
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
        .broker-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(4, 8, 18, 0.88);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
        }
        .broker-modal-toggle-btn {
          flex: 1; padding: 8px 0;
          font-family: ${FONT}; font-size: 9px; font-weight: 500;
          letter-spacing: 1px; text-transform: uppercase;
          border: none; cursor: pointer;
          transition: background .15s, color .15s;
        }
        .broker-modal-submit {
          width: 100%; padding: 11px;
          font-family: ${FONT}; font-size: 10px; font-weight: 500;
          letter-spacing: 1.2px; text-transform: uppercase;
          border-radius: 4px; cursor: pointer;
          transition: background .15s, border-color .15s, opacity .15s;
        }
        .broker-modal-submit:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>

        {/* ── Overlay ── */}
        <div className="broker-modal-overlay">
            <div style={{
          width:        "100%",
          maxWidth:     480,
          background:   C.bg1,
          border:       `1px solid ${C.border}`,
          borderRadius: 12,
          overflow:     "hidden",
        }}>

                {/* ── Header ── */}
                <div style={{
            padding:      "20px 28px 18px",
            borderBottom: `1px solid ${C.border}`,
            display:      "flex",
            alignItems:   "center",
            gap:          12,
          }}>
                    <div style={{
              width:        32, height: 32, borderRadius: "50%",
              background:   C.cyan + "15",
              border:       `1px solid ${C.cyan}30`,
              display:      "grid", placeItems: "center",
              flexShrink:   0,
            }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: C.cyan, opacity: .8 }} />
                    </div>
                    <div>
                        <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 500, color: C.text, letterSpacing: ".5px" }}>
                            Connect Your Broker
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, marginTop: 3, letterSpacing: ".3px" }}>
                            Alpaca · Paper trading required
                        </div>
                    </div>
                </div>

                {/* ── Body ── */}
                <div style={{ padding: "24px 28px" }}>

                    {/* Recommendation note */}
                    <div style={{
              background:   C.amber + "0d",
              border:       `1px solid ${C.amber}25`,
              borderRadius: 6,
              padding:      "10px 14px",
              marginBottom: 24,
              display:      "flex",
              gap:          10,
              alignItems:   "flex-start",
            }}>
                        <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.amber, marginTop: 5, flexShrink: 0 }} />
              <span style={{ fontFamily: FONT, fontSize: 9, color: C.amber, lineHeight: 1.6, letterSpacing: ".3px" }}>
                We recommend creating a <strong>dedicated Alpaca account</strong> for BerBul —
                separate from any account you use for personal trading.
              </span>
                    </div>

                    {/* Paper keys (required) */}
                    <FieldGroup label="Alpaca Paper API Key" required>
                        <Input
                            value={paperKey}
                            onChange={e => setPaperKey(e.target.value)}
                            placeholder="PK••••••••••••••••••••"
                            disabled={loading}
                        />
                    </FieldGroup>

                    <FieldGroup label="Alpaca Paper Secret Key" required>
                        <Input
                            value={paperSecret}
                            onChange={e => setPaperSecret(e.target.value)}
                            placeholder="••••••••••••••••••••••••••••••••••••••••"
                            type="password"
                            disabled={loading}
                        />
                    </FieldGroup>

                    {/* Divider */}
                    <div style={{
              display:      "flex",
              alignItems:   "center",
              gap:          12,
              margin:       "20px 0",
            }}>
                        <div style={{ flex: 1, height: 1, background: C.border }} />
              <span style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1px" }}>
                LIVE KEYS — OPTIONAL
              </span>
                        <div style={{ flex: 1, height: 1, background: C.border }} />
                    </div>

                    {/* Live keys (optional) */}
                    <FieldGroup label="Alpaca Live API Key">
                        <Input
                            value={liveKey}
                            onChange={e => setLiveKey(e.target.value)}
                            placeholder="PK••••••••••••••••••••"
                            disabled={loading}
                        />
                    </FieldGroup>

                    <FieldGroup label="Alpaca Live Secret Key">
                        <Input
                            value={liveSecret}
                            onChange={e => setLiveSecret(e.target.value)}
                            placeholder="••••••••••••••••••••••••••••••••••••••••"
                            type="password"
                            disabled={loading}
                        />
                    </FieldGroup>

                    {/* Trading mode toggle */}
                    <div style={{ marginBottom: 24 }}>
                        <Label>Trading Mode</Label>
                        <div style={{
                display:      "flex",
                border:       `1px solid ${C.border}`,
                borderRadius: 6,
                overflow:     "hidden",
              }}>
                            {["paper", "live"].map(m => {
                                const isSelected = mode === m;
                                const accent     = m === "paper" ? C.amber : C.green;
                                return (
                                <button
                                    key={m}
                                    className="broker-modal-toggle-btn"
                                    onClick={() => setMode(m)}
                                    disabled={loading}
                                    style={{
                        background: isSelected ? accent + "18" : "transparent",
                        color:      isSelected ? accent : C.meta,
                        borderRight: m === "paper" ? `1px solid ${C.border}` : "none",
                      }}
                                >
                                    {m === "paper" ? "📄 PAPER" : "⚡ LIVE"}
                                </button>
                                    );
                                })}
                        </div>
                        {mode === "live" && (
                        <div style={{
                  marginTop:  8,
                  fontFamily: FONT,
                  fontSize:   9,
                  color:      C.meta,
                  letterSpacing: ".3px",
                }}>
                            Live mode requires live keys above. You can switch modes later.
                        </div>
                            )}
                    </div>

                    {/* Consent checkbox */}
                    <div
                        onClick={() => !loading && setConsented(v => !v)}
                        style={{
                display:      "flex",
                alignItems:   "flex-start",
                gap:          12,
                marginBottom: 24,
                cursor:       loading ? "not-allowed" : "pointer",
                opacity:      loading ? 0.5 : 1,
              }}
                    >
                        <div style={{
                width:        16,
                height:       16,
                borderRadius: 3,
                border:       `1px solid ${consented ? C.green : C.border2}`,
                background:   consented ? C.green + "20" : "transparent",
                display:      "grid",
                placeItems:   "center",
                flexShrink:   0,
                marginTop:    1,
                transition:   "border-color .15s, background .15s",
              }}>
                            {consented && (
                            <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                <path d="M1 3.5L3.5 6L8 1" stroke={C.green} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                                )}
                        </div>
              <span style={{
                fontFamily:    FONT,
                fontSize:      10,
                color:         C.body,
                lineHeight:    1.6,
                letterSpacing: ".2px",
              }}>
                I understand that BerBul will have trading access to this Alpaca account
                and will place trades autonomously on my behalf.
              </span>
                    </div>

                    {/* Error */}
                    {error && (
                    <div style={{
                background:   C.red + "12",
                border:       `1px solid ${C.red}30`,
                borderRadius: 4,
                padding:      "9px 12px",
                marginBottom: 16,
                display:      "flex",
                alignItems:   "center",
                gap:          8,
              }}>
                        <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.red, flexShrink: 0 }} />
                        <span style={{ fontFamily: FONT, fontSize: 10, color: C.red }}>{error}</span>
                    </div>
                        )}

                    {/* Cancel — only shown when editing existing broker link */}
                    {onCancel && (
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        style={{
                                width:         "100%",
                                padding:       "10px",
                                marginBottom:  8,
                                fontFamily:    FONT,
                                fontSize:      10,
                                fontWeight:    500,
                                letterSpacing: "1.2px",
                                textTransform: "uppercase",
                                borderRadius:  4,
                                border:        `1px solid ${C.border}`,
                                background:    "transparent",
                                color:         C.meta,
                                cursor:        loading ? "not-allowed" : "pointer",
                                opacity:       loading ? 0.4 : 1,
                            }}
                    >
                        CANCEL
                    </button>
                        )}

                    {/* Submit */}
                    <button
                        className="broker-modal-submit"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        style={{
                background:  canSubmit ? C.cyan + "15" : "transparent",
                border:      `1px solid ${canSubmit ? C.cyan + "50" : C.border}`,
                color:       canSubmit ? C.cyan : C.meta,
              }}
                    >
                        {loading ? "LINKING BROKER..." : "LINK BROKER"}
                    </button>

                </div>
            </div>
        </div>
        </>
    );
}